"use client";

import { ArrowRight, Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  id: 1,
  question: "How quickly can you install a new AC unit?",
  answer: "We prioritize efficiency and quality. Once a site survey is completed and the system is selected, most domestic installations are completed within one to two days, ensuring minimal disruption to your home."
}, {
  id: 2,
  question: "Do you provide services for commercial properties?",
  answer: "Yes, Coastline Air Conditioning Services specializes in large-scale climate control solutions for offices, retail spaces, and industrial facilities across Sussex and Surrey, tailored to meet specific business requirements."
}, {
  id: 3,
  question: "How often should I service my air conditioning?",
  answer: "To maintain peak efficiency and air quality, we recommend professional servicing at least once a year. For high-usage commercial environments, bi-annual maintenance is often required."
}, {
  id: 4,
  question: "Are your systems energy efficient?",
  answer: "Absolutely. We install the latest inverter-driven heat pump technology, which significantly reduces energy consumption and lowers your carbon footprint compared to older cooling systems."
}, {
  id: 5,
  question: "What is included in your maintenance plan?",
  answer: "Our comprehensive maintenance includes filter cleaning, refrigerant level checks, electrical component inspection, and drainage system clearing to prevent leaks and ensure optimal performance."
}, {
  id: 6,
  question: "Do you offer emergency repair services?",
  answer: "We understand that climate control is vital. We offer priority response times for our contract clients to ensure your environment remains comfortable, even during peak summer heat."
}, {
  id: 7,
  question: "Is my property eligible for a free quote?",
  answer: "We provide free, no-obligation site surveys and consultations for all potential clients in our service area to ensure we recommend the most effective solution for your space."
}, {
  id: 8,
  question: "What areas do you cover?",
  answer: "Coastline Air Conditioning Services proudly serves the entirety of Sussex and Surrey. Our mobile engineering teams are strategically located to provide prompt service across these regions."
}];
export default function Inquiries() {
  const eyebrow = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const heading = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const para = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const list = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="4729" id="privacypolicy" className="bg-slate-50 py-24 md:py-40">
      <div className="mx-auto max-w-7xl px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-y-16 lg:gap-x-12">
          {}
          <div className="lg:col-span-4 lg:pr-6">
            {}
            <div ref={eyebrow.ref} className={`flex items-center gap-4 mb-8 transition-all duration-700 ease-out ${eyebrow.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="h-px w-10 bg-blue-600" aria-hidden="true" />
              <span className="font-default text-xs tracking-[0.28em] uppercase text-blue-600">COMMON ENQUIRIES</span>
            </div>

            {}
            <h2 ref={heading.ref} className={`font-default text-5xl md:text-6xl leading-tight mb-8 text-slate-900 transition-all duration-700 ease-out ${heading.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              Answers to your climate control questions.
            </h2>

            {}
            <p ref={para.ref} className={`font-default text-base leading-relaxed text-slate-600 mb-6 transition-all duration-700 ease-out delay-100 ${para.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              For specific technical requirements or a custom quote, our team is ready to assist you via our contact channels.
            </p>

            {}
            <Link to="/ContactUsPage" className={`inline-flex items-center gap-2 font-default text-sm text-slate-900 hover:text-blue-600 transition-colors duration-300 group ${para.isVisible ? "opacity-100" : "opacity-0"}`}>
              Contact our team{" "}
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>

          {}
          <div ref={list.ref} className={`lg:col-span-7 lg:col-start-6 transition-all duration-700 ease-out ${list.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Accordion type="single" collapsible className="w-full">
              {faqs.map((faq, i) => <AccordionItem key={faq.id} value={`item-${faq.id}`} data-index={i} className="border-b border-slate-200 py-8 last:border-b-0">
                  <AccordionTrigger className="flex items-start justify-between gap-6 p-0 hover:no-underline group">
                    <div className="flex items-start gap-4 flex-1 text-left">
                      <span className="font-default text-sm text-blue-600 shrink-0 mt-0.5">
                        {String(faq.id).padStart(2, "0")}
                      </span>
                      <h3 className="font-default text-2xl text-slate-900 group-hover:text-blue-600 transition-colors">
                        {faq.question}
                      </h3>
                    </div>
                    <Plus className="w-5 h-5 text-blue-600 shrink-0 mt-1 group-data-[state=open]:hidden" />
                  </AccordionTrigger>

                  <AccordionContent className="pl-10 pt-4">
                    <p className="font-default text-lg text-slate-600 leading-relaxed max-w-xl">
                      {faq.answer}
                    </p>
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>

            {}
            <div className="mt-12 pt-8 border-t border-slate-200">
              <p className="font-default text-sm italic text-slate-500">
                Need more information? Our support team responds to all inquiries within one business day.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}